import React from 'react';
import { PaymentCalculation } from '../types';

interface PricingCalculatorProps {
  calculation: PaymentCalculation;
  showBreakdown?: boolean;
  isNegotiated?: boolean;
}

const PricingCalculator: React.FC<PricingCalculatorProps> = ({
  calculation,
  showBreakdown = true,
  isNegotiated = false
}) => {
  return (
    <div className="pricing-calculator">
      <div className="pricing-summary">
        <h3>Pricing Summary</h3>
        
        {/* Simplified flat-rate display */}
        <div className="pricing-breakdown">
          <div className="flat-rate-display">
            <div className="flat-rate-price">
              <span className="currency">$</span>
              <span className="amount">379</span>
              <span className="period">/month</span>
            </div>
            <div className="flat-rate-description">
              Complete TableChat Solution
            </div>
          </div>

          <div className="whats-included">
            <h4>What's Included</h4>
            <ul>
              <li>✓ Unlimited staff accounts</li>
              <li>✓ All NFC table cards</li>
              <li>✓ Manager console & mobile app</li>
              <li>✓ Real-time messaging</li>
              <li>✓ Email support</li>
            </ul>
          </div>

          <div className="breakdown-divider"></div>
        </div>

        <div className="pricing-totals">
          <div className="total-item initial-payment">
            <span className="total-label">Pay Today</span>
            <span className="total-amount">$379.00</span>
          </div>
          <div className="total-description">
            First Month Service
          </div>

          <div className="total-item recurring-payment">
            <span className="total-label">Then Monthly</span>
            <span className="total-amount">$379.00</span>
          </div>
          <div className="total-description">
            Monthly Subscription
          </div>
        </div>

        {isNegotiated && (
          <div className="negotiated-pricing-notice">
            <p>✨ You have custom negotiated pricing for this package</p>
          </div>
        )}
      </div>

      <style>
        {`
        .pricing-calculator {
          background: #f8f9fa;
          border-radius: 12px;
          padding: 24px;
          border: 1px solid #e9ecef;
        }

        .pricing-summary h3 {
          margin: 0 0 20px 0;
          color: #2196F3;
          font-size: 1.25rem;
          font-weight: 600;
        }

        .breakdown-section {
          margin-bottom: 16px;
          position: relative;
        }

        .breakdown-section h4 {
          margin: 0 0 8px 0;
          font-size: 1rem;
          font-weight: 500;
          color: #495057;
        }

        .breakdown-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          border-bottom: 1px solid #dee2e6;
        }

        .breakdown-item:last-child {
          border-bottom: none;
        }

        .breakdown-item span:first-child {
          color: #6c757d;
        }

        .breakdown-item span:last-child {
          font-weight: 500;
          color: #212529;
        }

        .negotiated-badge {
          margin-top: 4px;
        }

        .badge {
          background: linear-gradient(45deg, #2196F3, #21CBF3);
          color: white;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .breakdown-divider {
          height: 1px;
          background: #dee2e6;
          margin: 16px 0;
        }

        .pricing-totals {
          margin-top: 16px;
        }

        .total-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }

        .initial-payment .total-label {
          font-size: 1.1rem;
          font-weight: 600;
          color: #2196F3;
        }

        .initial-payment .total-amount {
          font-size: 1.5rem;
          font-weight: 700;
          color: #2196F3;
        }

        .recurring-payment .total-label {
          font-size: 1rem;
          font-weight: 500;
          color: #28a745;
        }

        .recurring-payment .total-amount {
          font-size: 1.25rem;
          font-weight: 600;
          color: #28a745;
        }

        .total-description {
          font-size: 0.875rem;
          color: #6c757d;
          margin-bottom: 12px;
          text-align: left;
        }

        .negotiated-pricing-notice {
          margin-top: 16px;
          padding: 12px;
          background: linear-gradient(45deg, #e3f2fd, #f3e5f5);
          border-radius: 8px;
          border-left: 4px solid #2196F3;
        }

        .negotiated-pricing-notice p {
          margin: 0;
          color: #2196F3;
          font-weight: 500;
          font-size: 0.9rem;
        }

        .flat-rate-display {
          text-align: center;
          margin-bottom: 24px;
          padding: 20px;
          background: white;
          border-radius: 12px;
          border: 2px solid #2196F3;
        }

        .flat-rate-price {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 8px;
        }

        .flat-rate-price .currency {
          font-size: 1.5rem;
          color: #2196F3;
          font-weight: 600;
          margin-right: 4px;
        }

        .flat-rate-price .amount {
          font-size: 3rem;
          color: #2196F3;
          font-weight: 700;
          line-height: 1;
        }

        .flat-rate-price .period {
          font-size: 1.2rem;
          color: #6c757d;
          margin-left: 4px;
        }

        .flat-rate-description {
          font-size: 1rem;
          color: #495057;
          font-weight: 500;
        }

        .whats-included {
          margin-top: 20px;
          padding: 16px;
          background: white;
          border-radius: 8px;
        }

        .whats-included h4 {
          margin: 0 0 12px 0;
          font-size: 1rem;
          font-weight: 600;
          color: #495057;
        }

        .whats-included ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .whats-included li {
          padding: 6px 0;
          color: #6c757d;
          font-size: 0.9rem;
        }

        @media (max-width: 768px) {
          .pricing-calculator {
            padding: 16px;
          }

          .breakdown-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;
          }

          .total-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;
          }

          .total-description {
            text-align: left;
          }
        }
        `}
      </style>
    </div>
  );
};

export default PricingCalculator;
